/* eslint-disable  */
import { LabelWithDescription } from '@/components/LabelWithdescription';
import { LoadingOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteOutline, DragIndicator } from '@mui/icons-material';
import { Button, Card, Flex, Form, Input, notification, Select, Spin, Radio } from 'antd';
import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { OverViewCardForm } from '../OverviewCardForm';
import { QueryObserverResult, RefetchOptions, UseMutationResult } from '@tanstack/react-query';
import { InitialScenario } from '@/types/TPhoneForm';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { closeModal } from '@/store/modal/slice';
import { User } from '@/types/TAccounts';
import { FeatureFlag } from 'src/enums/EFeatureFlags';
import { useFeatureFlag } from 'src/Hooks/useFeatureFlag';
import { WorkflowType, FormActionType } from 'src/enums/EWorkflow';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import ShortUniqueId from 'short-unique-id';
import { getSelectedAgentId } from '@/store/account/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { TWorkflowAvailabilities } from '@/types/TWorkflowAvailabilities';
import { selectCanAddScenario } from '@/store/scenarioCounts/selector';
import { incrementScenarioCount } from '@/store/scenarioCounts/slice';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import ConfirmDeleteModal from '@/components/ConfirmDeleteModal/ConfirmDeleteModal';

const uid = new ShortUniqueId({ length: 10 });
interface PhoneWorkflowFormProps {
	initialValues?: InitialScenario;
	actionType: FormActionType;
	showAddFieldButton?: boolean;
	refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<User, Error>>;
	mutation: UseMutationResult<unknown, Error, any, unknown>;
	scenarioIndex?: number;
	disableSubmittion?: boolean;
	isDuplicate?: boolean;
}

export interface PhoneWorkflowFormRef {
	form: any;
}

const PhoneWorkflowForm = forwardRef<PhoneWorkflowFormRef, PhoneWorkflowFormProps>(
	(
		{
			initialValues,
			showAddFieldButton = true,
			refetch,
			actionType,
			mutation,
			scenarioIndex,
			disableSubmittion,
			isDuplicate,
		},
		ref,
	) => {
		const [form] = Form.useForm();
		const [isFieldTouchReset, setIsFieldTouchReset] = useState<boolean>(true);
		const dispatch = useAppDispatch();
		const isIntakeBeginningNaturalFlagEnabled = useFeatureFlag(
			FeatureFlag.INTAKE_BEGINNING_NATURAL,
		);
		const selectedAgentId = useAppSelector(getSelectedAgentId);
		const canAddScenario = useAppSelector(selectCanAddScenario);

		useEffect(() => {
			if (initialValues && actionType === FormActionType.EDIT) {
				form.setFieldsValue(initialValues);
				const formValues = {
					scenarios: initialValues.scenarios.map((scenario: any) => ({
						...scenario,
						availability: scenario.availability || null,
						configEnabled: scenario.configEnabled ?? false,
						fallbackMessage: scenario.fallbackMessage || '',
					})),
				};
				form.setFieldsValue(formValues);
			}

			if (actionType === FormActionType.ADD) {
				if (isDuplicate && initialValues) {
					form.setFieldsValue(initialValues);
				} else {
					form.resetFields();
				}
			}

			return () => {
				if (!isDuplicate) {
					form.resetFields();
				}
			};
		}, [initialValues, form, actionType, isDuplicate]);

		useImperativeHandle(
			ref,
			() => ({
				form,
			}),
			[form],
		);

		const handleSubmit = async (values: {
			scenarios: {
				id?: string;
				customScenario: string;
				final_collection_phrase: string;
				postExecutionPrompt?: string;
				questions: {
					question: string;
				}[];
				description?: string;
				intake_beginning_natural?: boolean;
				availability?: TWorkflowAvailabilities;
				configEnabled?: boolean;
				fallbackMessage?: string;
				boc_intake_post_execution_prompt?: string;
			}[];
		}) => {
			try {
				let formBuilders: {
					id: string;
					customScenario: string;
					final_collection_phrase: string;
					postExecutionPrompt?: string;
					questions: {
						question: string;
					};
					availability?: TWorkflowAvailabilities;
					configEnabled?: boolean;
					fallbackMessage?: string;
				}[] = [];

				if (
					values?.scenarios.filter((secenario) => secenario?.description === 'beginning').length > 1
				) {
					const beginningIndex = values?.scenarios
						.map((scenario) => scenario?.description)
						.reduceRight((lastIndex, description, index) => {
							return description === 'beginning' && lastIndex === -1 ? index : lastIndex;
						}, -1);

					if (beginningIndex > -1) {
						form.setFields([
							{
								name: ['scenarios', beginningIndex, 'description'],
								errors: ['There can be only one Beginning of call scenario'],
							},
						]);
					}

					return;
				}

				const updatedScenario = values?.scenarios.map((scenario) => ({
					id: scenario.id ?? uid.rnd(),
					customScenario:
						scenario?.description?.toLowerCase() === 'beginning'.toLowerCase()
							? ''
							: scenario.customScenario,
					description: scenario?.description
						? scenario?.description
						: scenario?.customScenario === 'Beginning of call'
							? 'beginning'
							: 'custom-scenario',
					final_collection_phrase: scenario?.final_collection_phrase,
					questions: scenario?.questions?.map((q) => q.question),
					postExecutionPrompt: scenario?.postExecutionPrompt,
					availability: scenario?.availability,
					configEnabled: scenario?.configEnabled ?? false,
					fallbackMessage: scenario?.fallbackMessage || '',
				}));
				if (initialValues?.scenarios && initialValues.scenarios?.[0]?.previousWorkflowData) {
					//@ts-expect-error ignore for now
					formBuilders = [...initialValues.scenarios[0].previousWorkflowData];
				}

				if (actionType === FormActionType.EDIT) {
					//@ts-expect-error ignore for now
					formBuilders.splice(scenarioIndex, updatedScenario.length, ...updatedScenario);
				} else if (actionType === FormActionType.ADD) {
					// @ts-expect-error ignore for now
					formBuilders = [...formBuilders, ...updatedScenario];
				}

				formBuilders = formBuilders.map((scenario) => ({
					...scenario,
					description: scenario.customScenario === '' ? 'beginning' : 'custom-scenario',
				}));

				/// Get the toggle value from the beginning scenario (if any)
				const naturalIntake = values.scenarios.find(
					(scenario) => scenario?.description === 'beginning',
				)?.intake_beginning_natural;
				const bocIntakePostExecutionPrompt = values.scenarios.find(
					(scenario) => scenario?.description === 'beginning',
				)?.boc_intake_post_execution_prompt;
				await mutation.mutateAsync({
					formBuilders,
					intake_beginning_natural: naturalIntake,
					agentId: selectedAgentId as string,
					boc_intake_post_execution_prompt: bocIntakePostExecutionPrompt,
				});

				refetch();
				notification.success({
					key: 'form-success',
					message: isDuplicate
						? 'Scenario duplicated successfully!'
						: 'Details has been saved successfully!',
					duration: 3,
				});

				setIsFieldTouchReset(false);
				dispatch(closeModal());
				await analyzeAndUpdateWorkflow(
					formBuilders,
					WorkflowType.INTAKE,
					dispatch,
					selectedAgentId as string,
				);

				if (actionType === FormActionType.ADD) {
					dispatch(incrementScenarioCount(WorkflowType.INTAKE));
				}
			} catch (err) {
				console.log(err);
				notification.error({
					message: 'Something went wrong, please try again later!',
				});
			}
		};

		const onValuesChange = (changedValues: any) => {
			if (changedValues.scenarios) {
				changedValues.scenarios.forEach((changedScenario: any, index: number) => {
					if (changedScenario?.description) {
						form.setFields([
							{
								name: ['scenarios', index, 'description'],
								errors: [],
							},
						]);
					}
				});
			}
		};

		const addFormHandler = (add: () => void) => {
			if (!canAddScenario) {
				notification.error({
					message: 'Scenario limit reached',
					description: 'Max scenarios (100) reached. Please Delete existing to add more.',
				});
				return;
			}
			add();
		};
		const handleConfirmDelete = (remove: (name: number) => void, name: number) => {
			ConfirmDeleteModal({
				itemName: `Scenario`,
				onConfirm: () => remove(name),
			});
		};

		const SortableItem = ({ children, id }: { children: React.ReactNode; id: string }) => {
			const { setNodeRef, transform, transition } = useSortable({ id });
			const style = { transform: CSS.Transform.toString(transform), transition };
			return (
				<div ref={setNodeRef} style={style}>
					{children}
				</div>
			);
		};

		const DragHandleComponent = ({ id }: { id: string }) => {
			const { attributes, listeners } = useSortable({ id });
			return (
				<div
					className="cursor-grab active:cursor-grabbing text-neutral-500 hover:text-neutral-800 pb-0 items-end"
					{...attributes}
					{...listeners}
					style={{ pointerEvents: 'auto' }}
				>
					<DragIndicator />
				</div>
			);
		};
		const handleDragEnd = (active: any, over: any, move: any) => {
			if (active.id !== over?.id && active.id && over?.id) {
				const oldIndex = typeof active.id === 'string' ? parseInt(active.id, 10) : NaN;
				const newIndex = over?.id && typeof over.id === 'string' ? parseInt(over.id, 10) : NaN;

				if (
					!isNaN(oldIndex) &&
					!isNaN(newIndex) &&
					oldIndex !== newIndex &&
					oldIndex >= 0 &&
					newIndex >= 0
				) {
					move(oldIndex, newIndex);
				}
			}
		};

		const FormItems = () => (
			<Form.List name="scenarios" initialValue={[{}]}>
				{(fields, { add, remove }) => (
					<>
						{fields.map(({ key, name, ...restField }, index) => {
							const hasBeginningCallScenario = !!(
								initialValues?.scenarios.length &&
								initialValues?.scenarios?.[0]?.previousWorkflowData &&
								initialValues.scenarios[0].previousWorkflowData.find(
									(scenario) =>
										//@ts-expect-error ignore for now
										scenario?.description === 'beginning' && scenario?.questions.length > 0,
								)
							);

							return (
								<Flex key={key} className="mb-2" gap={8}>
									<Card
										className="flex-1"
										title={
											!isDuplicate ? (
												<div className="flex justify-between">
													<span className="font-bold text-base">
														Scenario {(scenarioIndex ?? 0) + index + 1}
													</span>
													{fields.length > 1 && showAddFieldButton && (
														<Button
															type="primary"
															danger
															icon={<DeleteOutline />}
															onClick={() => handleConfirmDelete(remove, name)}
														>
															Delete
														</Button>
													)}
												</div>
											) : (
												<div className="flex justify-start">
													<span className="font-bold text-base">Customize Your Duplicate</span>
												</div>
											)
										}
									>
										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2"
												label="Scenario Description"
												description="Describe the scenario in which you would like to ask the caller some questions."
											/>
											<Form.Item
												{...restField}
												name={[name, 'description']}
												initialValue={!hasBeginningCallScenario ? 'beginning' : 'custom-scenario'}
											>
												<Select
													options={[
														{
															value: 'beginning',
															label: 'Beginning of Call',
															disabled: hasBeginningCallScenario || isDuplicate,
														},
														{ value: 'custom-scenario', label: 'Define a custom scenario' },
													]}
												/>
											</Form.Item>

											{isIntakeBeginningNaturalFlagEnabled ? (
												<Form.Item
													noStyle
													shouldUpdate={(prev, curr) => {
														return (
															prev.scenarios?.[name]?.description !==
															curr.scenarios?.[name]?.description
														);
													}}
												>
													{({ getFieldValue }) => {
														const scenarioDesc = getFieldValue(['scenarios', name, 'description']);
														if (scenarioDesc !== 'beginning') {
															return null;
														}
														return (
															<>
																<LabelWithDescription
																	customClassName="mt-2"
																	label="Trigger Intake"
																	description="Natural: Your AI receptionist will try to collect this information at the beginning of the call, but at a more natural time."
																/>
																<Form.Item
																	{...restField}
																	name={[name, 'intake_beginning_natural']}
																	initialValue={true}
																>
																	<Radio.Group>
																		<Radio value={true}>Natural</Radio>
																		<Radio value={false}>Immediate</Radio>
																	</Radio.Group>
																</Form.Item>
															</>
														);
													}}
												</Form.Item>
											) : (
												''
											)}

											<Form.Item noStyle shouldUpdate>
												{(formInstance) => {
													const workflowScenarios =
														(formInstance.getFieldsValue() &&
															formInstance.getFieldsValue()?.scenarios) ??
														[];

													const customScenarioIndex = workflowScenarios?.findIndex(
														(fieldItem) => fieldItem?.['description'] === 'custom-scenario',
													);

													return (
														customScenarioIndex === index && (
															<Form.Item
																{...restField}
																name={[name, 'customScenario']}
																rules={[
																	{
																		required: true,
																		message: 'Please provide a custom scenario description',
																	},
																]}
															>
																<Input.TextArea
																	className="mt-3"
																	placeholder="Ask the new user some qualifying questions. Execute this anytime the user mentions they are a new customer."
																/>
															</Form.Item>
														)
													);
												}}
											</Form.Item>
										</div>

										<div className="border-b">
											<LabelWithDescription
												customClassName="mt-2 mb-2"
												label="Questions"
												description="The questions you would like to ask the user in this scenario. Drag and Drop to reorder."
											/>
											<Form.List name={[name, 'questions']} initialValue={[{}]}>
												{(questionFields, { add: addQuestion, remove: removeQuestion, move }) => {
													const ids = questionFields.map((_, index) => index.toString());
													return (
														<DndContext
															key={`dnd-${questionFields.length}`}
															collisionDetection={closestCenter}
															onDragEnd={({ active, over }) => {
																handleDragEnd(active, over, move);
															}}
														>
															<SortableContext
																key={`sortable-${questionFields.length}`}
																items={ids}
																strategy={verticalListSortingStrategy}
															>
																{questionFields.map(
																	({ key: qKey, name: qName, ...qRestField }, index) => (
																		<SortableItem key={qKey} id={index.toString()}>
																			<Flex align="baseline" gap={4}>
																				{questionFields.length > 1 ? (
																					<DragHandleComponent id={index.toString()} />
																				) : (
																					<></>
																				)}
																				<Form.Item
																					className="flex-1"
																					{...qRestField}
																					name={[qName, 'question']}
																					rules={[
																						{
																							validator: (_, value) => {
																								if (!value || value.trim() === '') {
																									return Promise.reject("Question can't be blank");
																								}
																								return Promise.resolve();
																							},
																						},
																					]}
																				>
																					<Input placeholder="What is your first and last name?" />
																				</Form.Item>
																				{questionFields.length > 1 && (
																					<MinusCircleOutlined
																						className="text-red-500 hover:text-red-600"
																						onClick={() => removeQuestion(qName)}
																					/>
																				)}
																			</Flex>
																		</SortableItem>
																	),
																)}
															</SortableContext>

															<Form.Item>
																<Button
																	type="dashed"
																	onClick={() => addQuestion()}
																	block
																	icon={<PlusOutlined />}
																>
																	Add Question
																</Button>
															</Form.Item>
														</DndContext>
													);
												}}
											</Form.List>
										</div>

										<Form.Item noStyle shouldUpdate>
											{(formInstance) => {
												const currentScenario = formInstance.getFieldValue([
													'scenarios',
													name,
													'description',
												]);

												if (currentScenario === 'beginning') {
													return (
														<div className="space-y-2">
															<div className="border-b bg-white rounded-md">
																<LabelWithDescription
																	customClassName="mt-2 mb-2"
																	label="What should the AI do next?"
																	description="Specify instructions for the AI to follow after it completes the beginning of call intake."
																/>
																<Form.Item
																	className="mt-2 pt-2"
																	{...restField}
																	name={[name, 'boc_intake_post_execution_prompt']}
																>
																	<Input.TextArea
																		placeholder="e.g., Thank the caller, and let them know their information has been collected and submitted."
																		autoSize={{ minRows: 1, maxRows: 2 }}
																		data-testid="boc-intake-post-execution-input"
																	/>
																</Form.Item>
															</div>
														</div>
													);
												}

												return (
													currentScenario !== 'beginning' && (
														<div className="space-y-2">
															<div className="border-b bg-white rounded-md">
																<LabelWithDescription
																	customClassName="mt-2 mb-2"
																	label="What should the AI do next?"
																	description="Specify instructions for the AI to follow after it executes this workflow."
																/>
																<Form.Item
																	className="mt-2 pt-2"
																	{...restField}
																	name={[name, 'postExecutionPrompt']}
																>
																	<Input.TextArea
																		placeholder="e.g., Thank the caller for the information and ask if they would like to be transferred to customer support for assistance."
																		autoSize={{ minRows: 1, maxRows: 2 }}
																		data-testid="final-phrase-post-execution-input"
																	/>
																</Form.Item>
															</div>
														</div>
													)
												);
											}}
										</Form.Item>
									</Card>
								</Flex>
							);
						})}
						{showAddFieldButton && (
							<Form.Item>
								<Button
									type="dashed"
									onClick={() => addFormHandler(add)}
									block
									icon={<PlusOutlined />}
								>
									Add Intake Form Workflow
								</Button>
							</Form.Item>
						)}
					</>
				)}
			</Form.List>
		);

		return (
			<>
				<Spin
					spinning={mutation.isPending}
					indicator={<LoadingOutlined style={{ fontSize: '48px' }} spin />}
				>
					<OverViewCardForm
						form={form}
						onValuesChange={onValuesChange}
						initialValues={actionType === FormActionType.EDIT && initialValues}
						formItems={<FormItems />}
						handleSubmit={handleSubmit}
						isFieldTouchReset={isFieldTouchReset}
						disableSubmittion={disableSubmittion}
						hideUnsavedChangesModal={isDuplicate}
					/>
				</Spin>
			</>
		);
	},
);

export default PhoneWorkflowForm;
